import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  hikingPath,
  accountPath,
  storeAuthRedirect,
  storeReturnPathFromLocation,
} from '../utils/paths';
import AppSwitcher, { resolveActiveAppId } from './AppSwitcher';
import { SITE_NAME, SITE_APPS, formatAppTitle } from '../config/siteBranding';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import HikingLanding from './HikingLanding';
import HikingClubHome from './HikingClubHome';
import HikingTrailsPage from './HikingTrailsPage';
import HikingOutingScheduling from './HikingOutingScheduling';
import HikingOutingDetail from './HikingOutingDetail';
import PublicHikingOuting from './PublicHikingOuting';
import HikingClubsList from './HikingClubsList';
import HikingClubAdmin from './HikingClubAdmin';
import HikingProfile from './HikingProfile';
import HikingProfileRegistration from './HikingProfileRegistration';
import ClubRegistration from './ClubRegistration';
import MyClubs from './MyClubs';
import './MembershipStatusBanner.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!isAuthenticated) {
    storeAuthRedirect(window.location.pathname + window.location.search);
    return <Navigate to="/login" replace />;
  }
  return children;
};

const HikingApp = () => {
  const { isAuthenticated, user, logout, isAdmin, isClubCreator } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  const handleAuthNavClick = () => {
    storeReturnPathFromLocation(location.pathname, location.search);
    closeMobileMenu();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        !event.target.closest('.nav-menu') &&
        !event.target.closest('.mobile-menu-toggle')
      ) {
        closeMobileMenu();
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const activeAppId = resolveActiveAppId(location.pathname);
  const isPublicShare = location.pathname.includes('/public/outings/');

  useEffect(() => {
    setPageMeta({
      title: formatAppTitle(SITE_APPS.hiking.name),
      description: SITE_APPS.hiking.description,
      url: `${window.location.origin}${SITE_APPS.hiking.href}`,
    });
    return () => resetPageMeta();
  }, [activeAppId]);

  return (
    <>
      {!isPublicShare && (
        <nav className="navbar">
          <div className="nav-container">
            <div className="nav-brand-block">
              <Link to="/" className="nav-logo" onClick={closeMobileMenu}>
                {SITE_NAME}
              </Link>
              <AppSwitcher
                activeAppId={activeAppId}
                onNavigate={closeMobileMenu}
                variant="compact"
                className="nav-app-switcher"
              />
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div className={`nav-menu ${mobileMenuOpen ? 'nav-menu-open' : ''}`}>
              <Link to={hikingPath('/')} className="nav-link" onClick={closeMobileMenu}>
                Hiking home
              </Link>
              <Link to={hikingPath('/clubs/list')} className="nav-link" onClick={closeMobileMenu}>
                Clubs
              </Link>
              {isAuthenticated && (
                <>
                  <Link to={accountPath()} className="nav-link" onClick={closeMobileMenu}>
                    Account
                  </Link>
                  <Link to={hikingPath('/profile')} className="nav-link" onClick={closeMobileMenu}>
                    My profile
                  </Link>
                </>
              )}
              {isAuthenticated && (
                <Link to={hikingPath('/clubs/mine')} className="nav-link" onClick={closeMobileMenu}>
                  My clubs
                </Link>
              )}
              {isAuthenticated && (isAdmin() || isClubCreator()) && (
                <Link
                  to={hikingPath('/clubs/register')}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  Register club
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  <span className="nav-link nav-user-info" style={{ color: 'var(--color-brand)' }}>
                    Hi, {user?.name?.split(' ')[0] || 'there'}
                  </span>
                  <button
                    type="button"
                    className="nav-link nav-logout-link"
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link" onClick={handleAuthNavClick}>
                    Log in
                  </Link>
                  <Link to="/register" className="nav-link" onClick={handleAuthNavClick}>
                    Join
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      )}

      <main className={`main-content${isPublicShare ? ' main-content--public' : ''}`}>
        <Routes>
          <Route path="/" element={<HikingLanding />} />
          <Route path="/clubs/list" element={<HikingClubsList />} />
          <Route
            path="/clubs/mine"
            element={
              <ProtectedRoute>
                <MyClubs sportFilter="HIKING" basePath={hikingPath()} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs/register"
            element={
              <ProtectedRoute>
                <ClubRegistration defaultSport="HIKING" successBasePath={hikingPath()} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/complete"
            element={
              <ProtectedRoute>
                <HikingProfileRegistration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <HikingProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/clubs/:id" element={<HikingClubHome />} />
          <Route
            path="/clubs/:id/admin"
            element={
              <ProtectedRoute>
                <HikingClubAdmin />
              </ProtectedRoute>
            }
          />
          <Route path="/clubs/:id/trails" element={<HikingTrailsPage />} />
          <Route path="/clubs/:id/outings" element={<HikingOutingScheduling />} />
          <Route path="/outings/:id" element={<HikingOutingDetail />} />
          <Route path="/public/outings/:shareToken" element={<PublicHikingOuting />} />
          <Route path="*" element={<Navigate to={hikingPath('/')} replace />} />
        </Routes>
      </main>
    </>
  );
};

export default HikingApp;
